import React, {useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import wrap from 'wraptile';
import {AspectRatioType} from './SnapshotContainer';
import {curry} from 'ramda';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import {
    IconButton,
    Typography,
    Button,
    Dialog,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';
import PanoramaIcon from '@material-ui/icons/Panorama';
import ImageIcon from '@material-ui/icons/Image';
import {START_CREATE_SNAPSHOT} from './Redux/SnapshotActions';
import SpecificAspectRatioArtwork from './SpecificAspectRatioArtwork';
import {ApSelectionTool} from '@accurate-player/accurate-player-components-external';
import {ArtworkToCreate} from '@pluto/rest';
import Slide from '@material-ui/core/Slide';
import {TransitionProps} from '@material-ui/core/transitions';
import {DispatchAny} from '../../../@pluto/@types';

ApSelectionTool.register();

export interface CoordinatesState {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
}

export interface CreateArtworkDialogProps {
    snapshotId: number;
    artworkDialogIsOpen: boolean;
    artworkModalClose: () => void;
    currentArtwork: string;
}

export const deleteAspectRatioArtwork = curry((
    setPreviewImageAspectRatio: (value: string) => void,
    setCurrentAspectRatio: (value: AspectRatioType) => void,
    nextAvailableAspectRatio: AspectRatioType,
    previewNextAvailableAspectRatio: string,
    e: any,
) => {
    e.stopPropagation();

    if (previewNextAvailableAspectRatio) {
        setCurrentAspectRatio(nextAvailableAspectRatio);
    } else {
        setCurrentAspectRatio('1:1')
    }

    setPreviewImageAspectRatio('');
});

export const setAspectRatio = wrap((
    artworkRef: any,
    setPreviewImageAspectRatio: (value: string) => void,
    setCurrentAspectRatio: (value: AspectRatioType) => void,
    aspectRatio: AspectRatioType
) => {
    const artworkApi = artworkRef.current.api;
    const previewImageUrl = artworkApi.extractImageUrl();

    setPreviewImageAspectRatio(previewImageUrl);
    setCurrentAspectRatio(aspectRatio);
});

export const resetOnClosing = wrap((
    artworkModalClose: () => void,
    setCurrentAspectRatio: (val: string) => void,
    setPreviewImageAspectRatio169: (val: string) => void,
    setPreviewImageAspectRatio43: (val: string) => void,
) => {
    setCurrentAspectRatio('1:1');
    setPreviewImageAspectRatio169('');
    setPreviewImageAspectRatio43('');
    artworkModalClose();
});

export const handleCreateArtwork = wrap((
    id: number,
    coordinatesState43: CoordinatesState,
    coordinatesState169: CoordinatesState,
    previewImageAspectRatio43: string,
    previewImageAspectRatio169: string,
    dispatch: DispatchAny,
    onClose: () => void,
) => {
    const params: ArtworkToCreate[] = [];

    if (previewImageAspectRatio43) {
        params.push({
            artworkType: 'thumbnail_small',
            left: coordinatesState43.left,
            right: coordinatesState43.right,
            bottom: coordinatesState43.bottom,
            top: coordinatesState43.top,
            width: coordinatesState43.width,
            height: coordinatesState43.height,
        });
    }

    if (previewImageAspectRatio169) {
        params.push({
            artworkType: 'thumbnail_large',
            left: coordinatesState169.left,
            right: coordinatesState169.right,
            bottom: coordinatesState169.bottom,
            top: coordinatesState169.top,
            width: coordinatesState169.width,
            height: coordinatesState169.height,
        })
    }

    dispatch({
        type: START_CREATE_SNAPSHOT,
        payload: {
            id,
            params: params,
        }
    });

    onClose();
});

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function CreateArtworkDialog(props: CreateArtworkDialogProps) {
    const {
        artworkDialogIsOpen,
        artworkModalClose,
        currentArtwork,
        snapshotId,
    } = props;
    const dispatch = useDispatch();
    const [coordinatesState43, setCoordinatesState43] = useState<CoordinatesState>();
    const [coordinatesState169, setCoordinatesState169] = useState<CoordinatesState>();
    const [currentAspectRatio, setCurrentAspectRatio] = useState<AspectRatioType>('1:1')
    const [previewImageAspectRatio169, setPreviewImageAspectRatio169] = useState<string>('');
    const [previewImageAspectRatio43, setPreviewImageAspectRatio43] = useState<string>('');

    const artworkRef169 = useRef<ApSelectionTool>(null);
    const artworkRef43 = useRef<ApSelectionTool>(null);
    const mockArtworkRef11 = useRef<ApSelectionTool>(null);
    const onClose = resetOnClosing(
        artworkModalClose,
        setCurrentAspectRatio,
        setPreviewImageAspectRatio169,
        setPreviewImageAspectRatio43
    );
    const onCreateArtwork = handleCreateArtwork(
        snapshotId,
        coordinatesState43,
        coordinatesState169,
        previewImageAspectRatio43,
        previewImageAspectRatio169,
        dispatch,
        onClose,
    );

    return (
        <Dialog
            fullScreen
            className="create-artwork-dialog"
            open={artworkDialogIsOpen}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <MuiDialogTitle
                id="create-artwork-dialog-title"
                disableTypography
                className="create-artwork-dialog__title"
            >
                <IconButton
                    aria-label="close"
                    className="close-button"
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
                <Typography variant="h6">
                    Create artwork
                </Typography>
            </MuiDialogTitle>
            <div className="create-artwork-dialog__body">
                <div
                    className={classNames('create-artwork-dialog__body-overlay', {
                        hidden: currentAspectRatio !== '1:1'
                    })}
                >
                    Please select artwork type to start working
                </div>
                <div className="create-artwork-dialog__body__aspect-ratios">
                    <div
                        className={classNames('create-artwork-dialog__body__aspect-ratio create-artwork-dialog__body__aspect-ratio_16-9', {
                            active: currentAspectRatio === '16:9',
                            placeholder: !previewImageAspectRatio169
                        })}
                        onClick={setAspectRatio(
                            artworkRef169,
                            setPreviewImageAspectRatio169,
                            setCurrentAspectRatio,
                            '16:9',
                        )}
                    >
                        {!previewImageAspectRatio169 ? (
                            <>
                                <PanoramaIcon/>
                                <span>Screenshot 16:9</span>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    aria-label="close"
                                    className="delete-artwork"
                                    onClick={deleteAspectRatioArtwork(
                                        setPreviewImageAspectRatio169,
                                        setCurrentAspectRatio,
                                        '4:3',
                                        previewImageAspectRatio43
                                    )}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <img src={previewImageAspectRatio169}/>
                            </>
                        )}
                    </div>
                    <div
                        className={classNames('create-artwork-dialog__body__aspect-ratio create-artwork-dialog__body__aspect-ratio_4-3', {
                            active: currentAspectRatio === '4:3',
                            placeholder: !previewImageAspectRatio43,
                        })}
                        onClick={setAspectRatio(
                            artworkRef43,
                            setPreviewImageAspectRatio43,
                            setCurrentAspectRatio,
                            '4:3',
                        )}
                    >
                        {!previewImageAspectRatio43 ? (
                            <>
                                <ImageIcon/>
                                <span>Screenshot 4:3</span>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    aria-label="close"
                                    className="delete-artwork"
                                    onClick={deleteAspectRatioArtwork(
                                        setPreviewImageAspectRatio43,
                                        setCurrentAspectRatio,
                                        '16:9',
                                        previewImageAspectRatio169
                                    )}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <img src={previewImageAspectRatio43}/>
                            </>
                        )}
                    </div>
                </div>
                {currentAspectRatio === '1:1' ? (
                    <div className='create-artwork-dialog__body__wrapper active'>
                        <SpecificAspectRatioArtwork
                            ref={mockArtworkRef11}
                            currentArtwork={currentArtwork}
                            currentAspectRatio='1:1'
                        />
                    </div>
                ) : null}
                <div
                    className={classNames('create-artwork-dialog__body__wrapper', {
                        active: currentAspectRatio === '16:9'
                    })}
                >
                    <SpecificAspectRatioArtwork
                        ref={artworkRef169}
                        setCoordinatesState={setCoordinatesState169}
                        setPreviewImageAspectRatio={setPreviewImageAspectRatio169}
                        currentArtwork={currentArtwork}
                        currentAspectRatio='16:9'
                    />
                    <div
                        className={classNames('create-artwork-dialog__body__wrapper_overlay', {
                            active: currentAspectRatio === '16:9'
                        })}
                    >
                        Screenshot 16:9
                    </div>
                </div>
                <div
                    className={classNames('create-artwork-dialog__body__wrapper', {
                        active: currentAspectRatio === '4:3'
                    })}
                >
                    <SpecificAspectRatioArtwork
                        ref={artworkRef43}
                        setCoordinatesState={setCoordinatesState43}
                        setPreviewImageAspectRatio={setPreviewImageAspectRatio43}
                        currentArtwork={currentArtwork}
                        currentAspectRatio='4:3'
                    />
                    <div
                        className={classNames('create-artwork-dialog__body__wrapper_overlay', {
                            active: currentAspectRatio === '4:3'
                        })}
                    >
                        Screenshot 4:3
                    </div>
                </div>
                <div className="dialog__buttons">
                    <div className="dialog__buttons_cancel">
                        <Button
                            onClick={onClose}
                            variant="text"
                        >
                            Cancel
                        </Button>
                    </div>
                    <div className="dialog__buttons_confirm">
                        <Button
                            onClick={onCreateArtwork}
                            variant="outlined"
                        >
                            Create artwork
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default React.memo(CreateArtworkDialog);
